import React, { useEffect, useState } from "react";
import { gcRoiAPI } from "../../services/api";
import { queueAddErrorMessage } from "./gcRoiIntroductionWorkflow";

interface DrillRecord {
  vendorId: string;
  companyName: string;
  trade: string;
  isActiveBidder: boolean;
  renewalDue: boolean;
  liquidityRisk: boolean;
  liquidityRiskFlagBasis?: string | null;
  suretyBacked: boolean;
  qualificationExpirationDate: string | null;
  suretyAggregateCapacity?: number | null;
  suretySingleCapacity?: number | null;
  totalBids?: number | null;
  quickRatio?: number | null;
  daysCashOnHand?: number | null;
  workingCapital?: number | null;
  debtRatio?: number | null;
  debtToEquity?: number | null;
  netCashFlow?: number | null;
  riskEvaluationScore?: number | null;
  financialSource?: string | null;
  financialStatementDate?: string | null;
  financialStatementPeriodMonths?: number | null;
  financialStatementType?: string | null;
  financialYear?: number | null;
  pulledAt?: string | null;
  tradeTappRisks?: string[] | null;
  tradeTappStrengths?: string[] | null;
  whyHere?: string[] | null;
  recommendedPath?: string | null;
  recommendedPathLabel?: string | null;
  recommendedPathNote?: string | null;
  projectApprovals?: Array<{
    projectName?: string | null;
    approvalResponse?: string | null;
    approvedWithRmp?: boolean;
    subcontractAwardValue?: number | string | null;
    rmpRequirements?: string[] | null;
  }> | null;
}

interface DrilldownResponse {
  metricKey: string;
  metricLabel: string;
  source: string;
  disclaimer: string;
  count: number;
  records: DrillRecord[];
}

interface Props {
  isOpen: boolean;
  metricKey: string | null;
  metricLabel: string;
  onClose: () => void;
  onQueued: () => void;
}

interface RiskDriver {
  id: string;
  title: string;
  explanation: string;
  disposition: string;
  source: string;
  recency: string;
}

interface SupportMetric {
  label: string;
  value: string;
}

interface TradeTappMetric {
  label: string;
  value: string;
}

const SOURCE_PENDING = "Source Pending";
const DISMISSAL_REASONS = [
  ["FINANCIALS_OUTDATED", "Financials outdated — six months"],
  ["LARGE_SOPHISTICATED_TRADE_PARTNER", "Large, sophisticated trade partner — two years"],
  ["OTHER", "Other"],
] as const;

const DS: Record<string, React.CSSProperties> = {
  backdrop:    { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 200 },
  drawer:      { position: "fixed", top: 0, right: 0, width: "min(520px, 100vw)", height: "100vh", backgroundColor: "#0f172a", borderLeft: "1px solid #334155", zIndex: 201, display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif", color: "#f1f5f9" },
  header:      { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "1.25rem 1.5rem", borderBottom: "1px solid #334155", flexShrink: 0 },
  title:       { fontSize: "1rem", fontWeight: 700, margin: "0 0 0.2rem 0", color: "#f1f5f9" },
  titleSub:    { fontSize: "0.75rem", color: "#60a5fa", margin: 0, fontWeight: 500 },
  closeBtn:    { background: "none", border: "1px solid #334155", borderRadius: "0.375rem", color: "#94a3b8", cursor: "pointer", fontSize: "1.15rem", lineHeight: 1, padding: "0.15rem 0.45rem" },
  meta:        { padding: "0.75rem 1.5rem", backgroundColor: "#1e293b", borderBottom: "1px solid #334155", flexShrink: 0 },
  metaSource:  { color: "#64748b", fontSize: "0.7rem", margin: "0 0 0.2rem 0" },
  metaDisclm:  { color: "#475569", fontSize: "0.7rem", margin: 0, fontStyle: "italic" },
  body:        { flex: 1, overflowY: "auto" as const, padding: "1rem 1.5rem" },
  countRow:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" },
  countBadge:  { backgroundColor: "#1e3a5f", color: "#93c5fd", fontSize: "0.75rem", fontWeight: 600, padding: "0.25rem 0.625rem", borderRadius: "1rem", border: "1px solid #2563eb" },
  selAllBtn:   { background: "none", border: "none", color: "#60a5fa", fontSize: "0.75rem", cursor: "pointer", padding: 0, textDecoration: "underline" },
  row:         { display: "flex", alignItems: "flex-start", padding: "0.75rem", borderRadius: "0.65rem", border: "1px solid #334155", marginBottom: "0.5rem", transition: "background-color 0.1s, border-color 0.1s", flexDirection: "column" as const, gap: "0.55rem" },
  rowTop:      { display: "flex", alignItems: "flex-start", width: "100%", gap: "0.5rem" },
  rowName:     { fontSize: "0.85rem", fontWeight: 600, color: "#f1f5f9", margin: "0 0 0.25rem 0", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" as const },
  rowMeta:     { fontSize: "0.7rem", color: "#64748b", margin: 0, display: "flex", flexWrap: "wrap" as const, alignItems: "center", gap: "0.25rem" },
  expandBtn:   { background: "none", border: "1px solid #334155", borderRadius: "0.375rem", color: "#93c5fd", cursor: "pointer", fontSize: "0.68rem", fontWeight: 700, padding: "0.25rem 0.5rem", marginLeft: "auto", flexShrink: 0 },
  detail:      { width: "100%", borderTop: "1px solid #334155", paddingTop: "0.65rem" },
  metricGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(7.5rem, 1fr))", gap: "0.45rem", marginBottom: "0.65rem" },
  metricCard:  { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "0.5rem", padding: "0.5rem 0.55rem" },
  metricLabel: { color: "#64748b", fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" as const, margin: "0 0 0.2rem 0" },
  metricValue: { color: "#e2e8f0", fontSize: "0.78rem", fontWeight: 700, margin: 0 },
  driver:      { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "0.5rem", padding: "0.55rem 0.65rem", marginBottom: "0.4rem" },
  driverHead:  { display: "flex", justifyContent: "space-between", gap: "0.5rem", alignItems: "flex-start", marginBottom: "0.25rem" },
  driverTitle: { color: "#f1f5f9", fontSize: "0.78rem", fontWeight: 800, margin: 0 },
  driverDisp:  { color: "#93c5fd", fontSize: "0.65rem", fontWeight: 700, whiteSpace: "nowrap" as const },
  driverExpl:  { color: "#94a3b8", fontSize: "0.72rem", lineHeight: 1.4, margin: "0 0 0.35rem 0" },
  driverMeta:  { color: "#64748b", fontSize: "0.65rem", margin: 0 },
  queueBtn:    { width: "100%", marginTop: "0.45rem", padding: "0.65rem 0.75rem", backgroundColor: "#2563eb", color: "#f8fafc", border: "none", borderRadius: "0.45rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" },
  footer:      { padding: "1rem 1.5rem", borderTop: "1px solid #334155", flexShrink: 0 },
  addBtn:      { width: "100%", padding: "0.75rem 1rem", backgroundColor: "#2563eb", color: "#f1f5f9", border: "none", borderRadius: "0.5rem", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer" },
  muted:       { color: "#64748b", fontSize: "0.85rem" },
  errorStyle:  { color: "#f87171", fontSize: "0.85rem" },
  success:     { color: "#34d399", fontSize: "0.8rem", marginBottom: "0.5rem" },
  dismissBtn:     { width: "100%", marginTop: "0.45rem", padding: "0.65rem 0.75rem", backgroundColor: "transparent", color: "#fbbf24", border: "1px solid #92400e", borderRadius: "0.45rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" },
  dismissPanel:   { marginTop: "0.55rem", padding: "0.65rem", backgroundColor: "#17202d", border: "1px solid #334155", borderRadius: "0.45rem", display: "grid", gap: "0.45rem" },
  fieldLabel:     { color: "#94a3b8", fontSize: "0.68rem", fontWeight: 700 },
  fieldInput:     { width: "100%", boxSizing: "border-box" as const, backgroundColor: "#0f172a", border: "1px solid #475569", borderRadius: "0.3rem", color: "#e2e8f0", padding: "0.4rem", font: "inherit", fontSize: "0.75rem" },
};

function signalTag(color: "blue" | "amber" | "red" | "green"): React.CSSProperties {
  const map: Record<string, [string, string]> = {
    blue:  ["#1e3a5f", "#93c5fd"],
    amber: ["#451a03", "#fde68a"],
    red:   ["#450a0a", "#fca5a5"],
    green: ["#052e16", "#86efac"],
  };
  const [bg, fg] = map[color];
  return { backgroundColor: bg, color: fg, borderRadius: "0.25rem", fontSize: "0.65rem", fontWeight: 600, padding: "0.1rem 0.35rem" };
}

const alreadyQueuedTag: React.CSSProperties = {
  backgroundColor: "#1e293b", color: "#64748b", border: "1px solid #334155",
  borderRadius: "0.25rem", fontSize: "0.65rem", fontWeight: 600, padding: "0.1rem 0.35rem",
};

function formatCapacity(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return SOURCE_PENDING;
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Signed currency — returns null when source is null (never coerce to $0). */
export function formatSignedCurrency(value: number | null | undefined): string | null {
  if (!isFiniteNumber(value)) return null;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function formatRatio(value: number | null | undefined): string | null {
  if (!isFiniteNumber(value)) return null;
  return value.toFixed(2);
}

export function formatDaysCash(value: number | null | undefined): string | null {
  if (!isFiniteNumber(value)) return null;
  return `${Math.round(value)} days`;
}

function normalizeTradeTappTexts(values: string[] | null | undefined): string[] {
  if (!Array.isArray(values)) return [];
  return values.map((v) => String(v || "").trim()).filter(Boolean);
}

export function bondRelatedRequirementTexts(rec: DrillRecord): string[] {
  const approvals = Array.isArray(rec.projectApprovals) ? rec.projectApprovals : [];
  const texts: string[] = [];
  approvals.forEach((approval) => {
    (approval.rmpRequirements || []).forEach((raw) => {
      const text = String(raw || "").trim();
      const lower = text.toLowerCase();
      if (
        text &&
        (lower.includes("bond") || lower.includes("surety") || lower.includes("credit support") || lower.includes("letter of credit"))
      ) {
        texts.push(text);
      }
    });
  });
  return Array.from(new Set(texts));
}

export function projectContextSummary(rec: DrillRecord): string | null {
  const approvals = Array.isArray(rec.projectApprovals) ? rec.projectApprovals : [];
  const names = approvals
    .map((approval) => String(approval.projectName || "").trim())
    .filter(Boolean);
  if (names.length === 0) return null;
  return names.slice(0, 3).join(" · ");
}

/** TradeTapp display name for source_system values. */
export function tradeTappSourceLabel(rec: DrillRecord): string {
  const raw = (rec.financialSource && String(rec.financialSource).trim()) || "";
  if (!raw || /^tradetapp$/i.test(raw)) return "TradeTapp";
  return raw;
}

export function buildTradeTappSourceContext(rec: DrillRecord): string {
  const source = tradeTappSourceLabel(rec);
  const date = rec.financialStatementDate && String(rec.financialStatementDate).trim();
  const type = rec.financialStatementType && String(rec.financialStatementType).trim();
  if (date && type) return `Source: ${source} \u00B7 Financial statement dated ${date} \u00B7 ${type}`;
  if (date) return `Source: ${source} \u00B7 Financial statement dated ${date}`;
  if (type) return `Source: ${source} \u00B7 ${type}`;
  return `Source: ${source} \u00B7 Statement date unavailable in the current import`;
}

/**
 * Preferred TradeTapp metrics first, then other imported numeric signals.
 * Null/missing values are omitted — never render Source Pending metric cards.
 */
export function buildTradeTappFinancialMetrics(rec: DrillRecord): TradeTappMetric[] {
  const metrics: TradeTappMetric[] = [];
  const quick = formatRatio(rec.quickRatio ?? null);
  if (quick) metrics.push({ label: "Quick Ratio", value: quick });
  const days = formatDaysCash(rec.daysCashOnHand ?? null);
  if (days) metrics.push({ label: "Days of Cash on Hand", value: days });
  const wc = formatSignedCurrency(rec.workingCapital ?? null);
  if (wc) metrics.push({ label: "Working Capital", value: wc });
  const debt = formatRatio(rec.debtRatio ?? null);
  if (debt) metrics.push({ label: "Debt Ratio", value: debt });
  const dte = formatRatio(rec.debtToEquity ?? null);
  if (dte) metrics.push({ label: "Debt to Equity", value: dte });
  const ncf = formatSignedCurrency(rec.netCashFlow ?? null);
  if (ncf) metrics.push({ label: "Net Cash Flow", value: ncf });
  if (isFiniteNumber(rec.riskEvaluationScore)) {
    metrics.push({
      label: "Risk Evaluation Score",
      value: String(rec.riskEvaluationScore),
    });
  }
  return metrics;
}

export function buildSupportMetrics(rec: DrillRecord): SupportMetric[] {
  const metrics: SupportMetric[] = [];
  if (rec.qualificationExpirationDate) {
    metrics.push({
      label: "Qualification expiration",
      value: rec.qualificationExpirationDate,
    });
  }
  const agg = formatCapacity(rec.suretyAggregateCapacity ?? null);
  if (agg !== SOURCE_PENDING) {
    metrics.push({ label: "Aggregate surety", value: agg });
  }
  const single = formatCapacity(rec.suretySingleCapacity ?? null);
  if (single !== SOURCE_PENDING) {
    metrics.push({ label: "Single-project surety", value: single });
  }
  if (typeof rec.totalBids === "number" && Number.isFinite(rec.totalBids)) {
    metrics.push({ label: "Matched bids", value: String(rec.totalBids) });
  }
  return metrics;
}

export function buildRiskDrivers(rec: DrillRecord, source: string): RiskDriver[] {
  const drivers: RiskDriver[] = [];
  if (rec.liquidityRisk) {
    drivers.push({
      id: "liquidity",
      title: "Liquidity Risk",
      explanation:
        "TradeTapp supplied a liquidity-risk flag for this trade partner. ConstructPay is not inventing a threshold or underwriting conclusion.",
      disposition: "Flagged",
      source: tradeTappSourceLabel(rec),
      recency: rec.financialStatementDate
        ? `Statement ${rec.financialStatementDate}`
        : "Statement date unavailable in the current import",
    });
  }
  if (rec.renewalDue) {
    drivers.push({
      id: "renewal",
      title: "Renewal Due",
      explanation: rec.qualificationExpirationDate
        ? `Qualification renewal is due. Stored qualification expiration date is ${rec.qualificationExpirationDate}.`
        : "Qualification renewal is due in the imported GC data snapshot. An expiration date is not available for this record.",
      disposition: "Due",
      source,
      recency: rec.qualificationExpirationDate
        ? `Expires ${rec.qualificationExpirationDate}`
        : SOURCE_PENDING,
    });
  }
  if (rec.suretyBacked) {
    const agg = formatCapacity(rec.suretyAggregateCapacity ?? null);
    const single = formatCapacity(rec.suretySingleCapacity ?? null);
    drivers.push({
      id: "surety",
      title: "Surety Signal",
      explanation:
        agg !== SOURCE_PENDING || single !== SOURCE_PENDING
          ? `Surety capacity is on file (aggregate ${agg}; single-project ${single}). Presence of capacity is not a bonding or award decision.`
          : "Surety-backed indicator is present in the imported GC data snapshot. Capacity amounts are not available for this record.",
      disposition: "On file",
      source,
      recency: SOURCE_PENDING,
    });
  }
  if (rec.isActiveBidder) {
    drivers.push({
      id: "active-bidder",
      title: "Active Bidder",
      explanation:
        "A BuildingConnected company match is present for this trade partner in the imported GC data snapshot. No additional bid activity state is inferred beyond that match.",
      disposition: "Matched",
      source,
      recency: SOURCE_PENDING,
    });
  }
  if (drivers.length === 0) {
    drivers.push({
      id: "source-pending",
      title: "Source Pending",
      explanation:
        "No additional source-backed risk drivers are available for this trade partner beyond the selected portfolio signal.",
      disposition: SOURCE_PENDING,
      source,
      recency: SOURCE_PENDING,
    });
  }
  return drivers;
}

export default function GcRoiDrillDrawer({ isOpen, metricKey, metricLabel, onClose, onQueued }: Props) {
  const [data, setData]             = useState<DrilldownResponse | null>(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [selected, setSelected]     = useState<Set<string>>(new Set());
  const [queuedIds, setQueuedIds]   = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [adding, setAdding]         = useState(false);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState("FINANCIALS_OUTDATED");
  const [dismissComment, setDismissComment] = useState("");
  const [dismissUntil, setDismissUntil] = useState("");
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!isOpen || !metricKey) return;
    setLoading(true);
    setError(null);
    setData(null);
    setSelected(new Set());
    setQueuedIds(new Set());
    setExpandedIds(new Set());
    setAddSuccess(null);
    setDismissingId(null);
    setDismissError(null);

    Promise.all([
      (gcRoiAPI as any).getDrilldown(metricKey),
      (gcRoiAPI as any).getQueue(),
      (gcRoiAPI as any).getActiveCandidateDispositions().catch(() => ({ data: [] })),
    ])
      .then(([drillRes, queueRes]: any[]) => {
        setData(drillRes.data);
        const ids = new Set<string>(
          (queueRes.data || []).map((item: any) => item.vendor_id as string)
        );
        setQueuedIds(ids);

      })
      .catch((err: any) => setError(err?.message ?? "Failed to load vendors"))
      .finally(() => setLoading(false));
  }, [isOpen, metricKey]);

  const toggle = (id: string) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleExpand = (id: string) =>
    setExpandedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  const toggleAll = () => {
    if (!data) return;
    const selectable = data.records.filter(r => !queuedIds.has(r.vendorId)).map(r => r.vendorId);
    const allSelected = selectable.length > 0 && selectable.every(id => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(selectable));
  };

  const queueVendors = async (vendors: { vendorId: string; companyName: string }[]) => {
    if (!data || vendors.length === 0) return;
    setAdding(true);
    setAddSuccess(null);
    try {
      const res = await (gcRoiAPI as any).addToQueue({ metricKey: data.metricKey, vendors });
      const added  = res.data?.added        ?? 0;
      const dupes  = res.data?.alreadyQueued ?? 0;

      setQueuedIds(prev => {
        const next = new Set(prev);
        vendors.forEach(v => next.add(v.vendorId));
        return next;
      });
      setSelected(new Set());

      if (added > 0 && dupes === 0) {
        setAddSuccess(
          `${added} vendor${added !== 1 ? "s" : ""} added to Introduction Queue. No outreach was triggered.`
        );
      } else if (added > 0 && dupes > 0) {
        setAddSuccess(
          `${added} added. ${dupes} already in queue. No outreach was triggered for new placements.`
        );
      } else {
        setAddSuccess("Selected vendors are already in the queue.");
      }
      const drillRes = await (gcRoiAPI as any).getDrilldown(data.metricKey);
      setData(drillRes.data);
      setExpandedIds(prev => {
        const next = new Set(prev);
        vendors.forEach(v => next.delete(v.vendorId));
        return next;
      });
      onQueued();
    } catch (e: any) {
      setError(queueAddErrorMessage(e));
    } finally {
      setAdding(false);
    }
  };

  const handleAdd = async () => {
    if (!data || selected.size === 0) return;
    const vendors = data.records
      .filter(r => selected.has(r.vendorId))
      .map(r => ({ vendorId: r.vendorId, companyName: r.companyName }));
    await queueVendors(vendors);
  };

  const openDismiss = (vendorId: string) => {
    setDismissingId(vendorId);
    setDismissReason("FINANCIALS_OUTDATED");
    setDismissComment("");
    setDismissUntil("");
    setDismissError(null);
  };

  const dismissCandidate = async (rec: DrillRecord) => {
    if (!data || dismissing) return;
    if (dismissReason === "OTHER" && !dismissComment.trim()) {
      setDismissError("Add a comment when selecting Other.");
      return;
    }
    if (dismissReason === "OTHER") {
      const today = new Date().toISOString().slice(0, 10);
      if (!dismissUntil || dismissUntil <= today) {
        setDismissError("Choose a future dismissal date.");
        return;
      }
    }
    setDismissing(true);
    setDismissError(null);
    try {
      await (gcRoiAPI as any).createCandidateDisposition({
        metricKey: data.metricKey,
        vendorId: rec.vendorId,
        reasonCode: dismissReason,
        comment: dismissComment.trim() || null,
        dismissUntil:
          dismissReason === "OTHER" && dismissUntil
            ? `${dismissUntil}T12:00:00.000Z`
            : dismissUntil || null,
      });
      const drillRes = await (gcRoiAPI as any).getDrilldown(data.metricKey);
      setData(drillRes.data);
      setSelected(prev => { const next = new Set(prev); next.delete(rec.vendorId); return next; });
      setExpandedIds(prev => { const next = new Set(prev); next.delete(rec.vendorId); return next; });
      setDismissingId(null);
    } catch (e: any) {
      const status = e?.response?.status;
      const apiError = e?.response?.data?.error;
      setDismissError(
        apiError
          || (status === 500
            ? "Dismissal could not be saved (server error). Try again after the privilege fix is deployed."
            : null)
          || e?.message
          || "Failed to dismiss candidate"
      );
    } finally {
      setDismissing(false);
    }
  };

  if (!isOpen) return null;

  const selectableCount = data ? data.records.filter(r => !queuedIds.has(r.vendorId)).length : 0;

  return (
    <>
      <div style={DS.backdrop} onClick={onClose} />
      <div style={DS.drawer} data-testid="gc-roi-drill-drawer">

        <div style={DS.header}>
          <div style={{ minWidth: 0 }}>
            <p style={DS.title}>{metricLabel}</p>
            <p style={DS.titleSub}>Review evidence, then add to Introduction Queue if warranted</p>
          </div>
          <button style={DS.closeBtn} onClick={onClose} aria-label="Close" type="button">
            {"\u00D7"}
          </button>
        </div>

        {data && (
          <div style={DS.meta}>
            <p style={DS.metaSource}>Source: {data.source}</p>
            <p style={DS.metaDisclm}>{data.disclaimer}</p>
          </div>
        )}

        <div style={DS.body}>
          {loading    && <p style={DS.muted}>Loading vendors...</p>}
          {error      && <p style={DS.errorStyle}>Error: {error}</p>}
          {addSuccess && <p style={DS.success}>{addSuccess}</p>}

          {data && (
            <>
              <div style={DS.countRow}>
                <span style={DS.countBadge}>
                  {data.count} vendor{data.count !== 1 ? "s" : ""}
                  {queuedIds.size > 0 && ` \u00B7 ${queuedIds.size > data.count ? data.count - selectableCount : data.count - selectableCount} already queued`}
                </span>
                {selectableCount > 0 && (
                  <button style={DS.selAllBtn} onClick={toggleAll} type="button">
                    {data.records.filter(r => !queuedIds.has(r.vendorId)).every(r => selected.has(r.vendorId)) && selected.size > 0
                      ? "Deselect all"
                      : "Select all"}
                  </button>
                )}
              </div>

              {data.records.map(rec => {
                const isQueued = queuedIds.has(rec.vendorId);
                const isSel    = selected.has(rec.vendorId);
                const isExpanded = expandedIds.has(rec.vendorId);
                const metrics = buildSupportMetrics(rec);
                const tradeTappMetrics = buildTradeTappFinancialMetrics(rec);
                const tradeTappRisks = normalizeTradeTappTexts(rec.tradeTappRisks);
                const tradeTappStrengths = normalizeTradeTappTexts(rec.tradeTappStrengths);
                const sourceContext = buildTradeTappSourceContext(rec);
                const drivers = buildRiskDrivers(rec, data.source);
                const whyHere = Array.isArray(rec.whyHere) ? rec.whyHere.filter(Boolean) : [];
                const bondReqs = bondRelatedRequirementTexts(rec);
                const projectSummary = projectContextSummary(rec);
                const wc = formatSignedCurrency(rec.workingCapital ?? null);
                const isBondReady = data.metricKey === "BONDREADY_REVIEW_CANDIDATES";
                const expandLabel = isBondReady
                  ? (isExpanded ? "Hide BondReady evidence" : "Review for BondReady")
                  : (isExpanded ? "Hide evidence" : "Review evidence");
                return (
                  <div
                    key={rec.vendorId}
                    data-testid="gc-roi-drill-row"
                    data-expanded={isExpanded ? "true" : "false"}
                    style={{
                      ...DS.row,
                      backgroundColor: isQueued ? "#131e2e" : isSel || isExpanded ? "#1e3a5f" : "#1e293b",
                      borderColor:     isQueued ? "#1e293b" : isSel || isExpanded ? "#3b82f6" : "#334155",
                      opacity:         isQueued ? 0.8 : 1,
                    }}
                  >
                    <div
                      style={{ ...DS.rowTop, cursor: isQueued ? "default" : "pointer" }}
                      onClick={() => { if (!isQueued) toggle(rec.vendorId); }}
                    >
                      <input
                        type="checkbox"
                        checked={isQueued || isSel}
                        disabled={isQueued}
                        onChange={() => { if (!isQueued) toggle(rec.vendorId); }}
                        onClick={e => e.stopPropagation()}
                        style={{ marginTop: "0.15rem", cursor: isQueued ? "not-allowed" : "pointer", accentColor: "#3b82f6", flexShrink: 0 }}
                        aria-label={`Select ${rec.companyName}`}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={DS.rowName}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rec.companyName}</span>
                          {isQueued && <span style={alreadyQueuedTag}>Already in Queue</span>}
                        </p>
                        <p style={DS.rowMeta}>
                          <span>{rec.trade || SOURCE_PENDING}</span>
                          {rec.isActiveBidder && <span style={signalTag("blue")}>Active Bidder</span>}
                          {rec.renewalDue     && <span style={signalTag("amber")}>Renewal Due</span>}
                          {rec.liquidityRisk  && <span style={signalTag("red")}>Liquidity Risk</span>}
                          {rec.suretyBacked   && <span style={signalTag("green")}>Surety</span>}
                        </p>
                        {isBondReady && whyHere[0] && (
                          <p style={{ ...DS.driverExpl, margin: "0.35rem 0 0" }} data-testid="gc-roi-why-review">
                            Why review: {whyHere[0]}
                          </p>
                        )}
                        {isBondReady && (
                          <p style={{ ...DS.rowMeta, marginTop: "0.25rem" }}>
                            {typeof rec.totalBids === "number" ? `${rec.totalBids} matched bids` : "Bid activity unavailable"}
                            {" · "}
                            single {formatCapacity(rec.suretySingleCapacity ?? null)}
                            {" · "}
                            aggregate {formatCapacity(rec.suretyAggregateCapacity ?? null)}
                            {wc ? ` · WC ${wc}` : ""}
                            {projectSummary ? ` · ${projectSummary}` : ""}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        style={DS.expandBtn}
                        aria-expanded={isExpanded}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(rec.vendorId);
                        }}
                      >
                        {expandLabel}
                      </button>
                    </div>

                    {isExpanded && (
                      <div style={DS.detail} data-testid="gc-roi-risk-detail">
                        {whyHere.length > 0 && (
                          <div data-testid="gc-roi-why-here" style={{ marginBottom: "0.65rem" }}>
                            <p style={{ ...DS.metricLabel, marginBottom: "0.35rem" }}>Why this trade partner is here</p>
                            <ul style={{ margin: 0, paddingLeft: 18, color: "#d5dbe2", fontSize: 12 }}>
                              {whyHere.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {rec.recommendedPathLabel && (
                          <div data-testid="gc-roi-recommended-path" style={{ ...DS.driver, marginBottom: "0.65rem" }}>
                            <p style={{ ...DS.metricLabel, marginBottom: "0.2rem" }}>Recommended ConstructPay path</p>
                            <p style={DS.driverTitle}>{rec.recommendedPathLabel}</p>
                            {rec.recommendedPathNote ? (
                              <p style={DS.driverExpl}>{rec.recommendedPathNote}</p>
                            ) : null}
                          </div>
                        )}
                        <p style={{ ...DS.driverExpl, marginBottom: "0.45rem" }}>
                          TradeTapp identified the financial signals shown below. ConstructPay is
                          presenting the source-provided risks and supporting metrics for GC review.
                        </p>
                        <p style={{ ...DS.driverMeta, marginBottom: "0.55rem" }} data-testid="gc-roi-tradetapp-source">
                          {sourceContext}
                        </p>

                        {tradeTappMetrics.length > 0 && (
                          <div data-testid="gc-roi-tradetapp-metrics" style={{ marginBottom: "0.65rem" }}>
                            <p style={{ ...DS.metricLabel, marginBottom: "0.35rem" }}>
                              TradeTapp financial metrics
                            </p>
                            <div style={DS.metricGrid}>
                              {tradeTappMetrics.map((m) => (
                                <div key={m.label} style={DS.metricCard} data-testid="gc-roi-tradetapp-metric">
                                  <p style={DS.metricLabel}>{m.label}</p>
                                  <p style={DS.metricValue}>{m.value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(tradeTappRisks.length > 0 || tradeTappStrengths.length > 0) && (
                          <div data-testid="gc-roi-tradetapp-risks-strengths" style={{ marginBottom: "0.65rem" }}>
                            <p style={{ ...DS.metricLabel, marginBottom: "0.35rem" }}>
                              {"TradeTapp Financial Risks & Strengths"}
                            </p>
                            {tradeTappRisks.map((text) => (
                              <div key={`risk-${text}`} style={DS.driver}>
                                <div style={DS.driverHead}>
                                  <p style={DS.driverTitle}>Risk</p>
                                  <span style={DS.driverDisp}>TradeTapp</span>
                                </div>
                                <p style={DS.driverExpl}>{text}</p>
                              </div>
                            ))}
                            {tradeTappStrengths.map((text) => (
                              <div key={`strength-${text}`} style={DS.driver}>
                                <div style={DS.driverHead}>
                                  <p style={DS.driverTitle}>Strength</p>
                                  <span style={DS.driverDisp}>TradeTapp</span>
                                </div>
                                <p style={DS.driverExpl}>{text}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {bondReqs.length > 0 && (
                          <div data-testid="gc-roi-bond-rmp" style={{ marginBottom: "0.65rem" }}>
                            <p style={{ ...DS.metricLabel, marginBottom: "0.35rem" }}>Bond-related RMP evidence</p>
                            <ul style={{ margin: 0, paddingLeft: 18, color: "#d5dbe2", fontSize: 12 }}>
                              {bondReqs.map((text) => (
                                <li key={text}>{text}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {metrics.length > 0 && (
                          <div style={{ marginBottom: "0.65rem" }}>
                            <p style={{ ...DS.metricLabel, marginBottom: "0.35rem" }}>
                              Portfolio context
                            </p>
                            <div style={DS.metricGrid}>
                              {metrics.map((m) => (
                                <div key={m.label} style={DS.metricCard}>
                                  <p style={DS.metricLabel}>{m.label}</p>
                                  <p style={DS.metricValue}>{m.value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {drivers.map((d) => (
                          <div key={d.id} style={DS.driver} data-testid="gc-roi-risk-driver">
                            <div style={DS.driverHead}>
                              <p style={DS.driverTitle}>{d.title}</p>
                              <span style={DS.driverDisp}>{d.disposition}</span>
                            </div>
                            <p style={DS.driverExpl}>{d.explanation}</p>
                            <p style={DS.driverMeta}>
                              Source: {d.source} {"\u00B7"} {d.recency}
                            </p>
                          </div>
                        ))}
                        {!isQueued && (
                          <>
                            <button type="button" style={{ ...DS.queueBtn, opacity: adding ? 0.45 : 1 }} disabled={adding} onClick={() => queueVendors([{ vendorId: rec.vendorId, companyName: rec.companyName }])}>
                              Add to Introduction Queue
                            </button>
                            <button type="button" style={DS.dismissBtn} onClick={() => openDismiss(rec.vendorId)}>Dismiss candidate</button>
                          </>
                        )}
                        {dismissingId === rec.vendorId && (
                          <div style={DS.dismissPanel} data-testid="gc-roi-dismiss-candidate">
                            <label style={DS.fieldLabel}>Dismissal reason
                              <select aria-label="Dismissal reason" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} style={DS.fieldInput}>
                                {DISMISSAL_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                              </select>
                            </label>
                            {dismissReason === "OTHER" && <label style={DS.fieldLabel}>Dismissal comment<textarea aria-label="Dismissal comment" value={dismissComment} onChange={(e) => setDismissComment(e.target.value)} style={DS.fieldInput} /></label>}
                            {dismissReason === "OTHER" && (
                              <label style={DS.fieldLabel}>
                                Dismiss until
                                <span style={{ display: "block", color: "#8d9aaa", fontWeight: 500, margin: "0.15rem 0 0.35rem" }}>
                                  Use the calendar control — avoid typing mm/dd/yyyy by hand.
                                </span>
                                <input
                                  aria-label="Dismiss until"
                                  type="date"
                                  value={dismissUntil}
                                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                                  onChange={(e) => setDismissUntil(e.target.value)}
                                  onClick={(e) => {
                                    const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                                    try { el.showPicker?.(); } catch { /* browser may ignore if not user-activated */ }
                                  }}
                                  onFocus={(e) => {
                                    const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
                                    try { el.showPicker?.(); } catch { /* ignore */ }
                                  }}
                                  style={{ ...DS.fieldInput, cursor: "pointer", colorScheme: "dark" }}
                                />
                              </label>
                            )}
                            {dismissError && <p style={DS.errorStyle}>{dismissError}</p>}
                            <button type="button" style={DS.dismissBtn} disabled={dismissing} onClick={() => dismissCandidate(rec)}>{dismissing ? "Dismissing..." : "Confirm dismissal"}</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {data.count === 0 && <p style={DS.muted}>No vendors found for this metric.</p>}

            </>
          )}
        </div>

        {data && selectableCount > 0 && (
          <div style={DS.footer}>
            <button
              style={{ ...DS.addBtn, opacity: selected.size === 0 || adding ? 0.45 : 1, cursor: selected.size === 0 || adding ? "not-allowed" : "pointer" }}
              disabled={selected.size === 0 || adding}
              onClick={handleAdd}
              type="button"
            >
              {adding
                ? "Adding..."
                : selected.size > 0
                  ? `Add ${selected.size} vendor${selected.size !== 1 ? "s" : ""} to Introduction Queue`
                  : "Select vendors to add to Introduction Queue"}
            </button>
          </div>
        )}

        {data && selectableCount === 0 && data.count > 0 && (
          <div style={DS.footer}>
            <p style={{ ...DS.muted, textAlign: "center", margin: 0, fontSize: "0.8rem" }}>
              All vendors in this metric are already in the Introduction Queue.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
